import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { Trophy, Flame } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "./ui/select";

const RANGE_OPTIONS = [
  { label: "Hoy", days: 1 },
  { label: "Últimos 7 días", days: 7 },
  { label: "Últimos 30 días", days: 30 },
];

export default function NumberRankings() {
  const [days, setDays] = useState(7);
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState({ top_sold: [], top_winners: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days, limit: 10 };
      if (branchId) params.branch_id = branchId;
      const { data } = await api.get("/dashboard/number-rankings", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [days, branchId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="number-rankings">
      <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
            Ranking de números
          </div>
          <div className="text-lg font-black tracking-tight text-slate-950" style={{ fontFamily: "Manrope" }}>
            Más jugados y más ganadores
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-9 rounded-sm border-slate-300 text-sm w-40" data-testid="ranking-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.days} value={String(o.days)}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {branches.length > 1 && (
            <Select value={branchId || "all"} onValueChange={(v) => setBranchId(v === "all" ? "" : v)}>
              <SelectTrigger className="h-9 rounded-sm border-slate-300 text-sm w-48" data-testid="ranking-branch">
                <SelectValue placeholder="Todas las bancas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las bancas</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
        <Panel
          icon={Flame}
          title="Más vendidos"
          accent="text-orange-600"
          rows={data.top_sold}
          loading={loading}
          empty="Aún no hay ventas en este rango"
          testId="ranking-sold"
          valueLabel="Total apostado"
          valueRender={(r) => `RD$ ${r.total_amount.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          subRender={(r) => `${r.times_played} jugadas`}
        />
        <Panel
          icon={Trophy}
          title="Más ganadores"
          accent="text-green-700"
          rows={data.top_winners}
          loading={loading}
          empty="Sin premios pagados en este rango"
          testId="ranking-winners"
          valueLabel="Total pagado"
          valueRender={(r) => `RD$ ${r.total_prize.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          subRender={(r) => `${r.wins} veces ganó`}
        />
      </div>
    </div>
  );
}

function Panel({ icon: Icon, title, accent, rows, loading, empty, testId, valueLabel, valueRender, subRender }) {
  return (
    <div className="p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${accent}`} />
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{title}</div>
      </div>
      {loading ? (
        <div className="text-sm text-slate-400 py-6 text-center">Cargando…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center">{empty}</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r, i) => (
            <div key={`${r.game_name}-${r.display}-${i}`} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0" data-testid={`${testId}-row-${i}`}>
              <div className={`w-6 text-xs font-black tabular-nums ${i < 3 ? accent : "text-slate-400"}`}>#{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-mono font-bold text-slate-950 text-base tracking-wider">{r.display}</div>
                <div className="text-[10px] text-slate-500 truncate">{r.game_name} · {r.play_type}</div>
              </div>
              <div className="text-right whitespace-nowrap">
                <div className="font-mono font-bold text-sm text-slate-950">{valueRender(r)}</div>
                <div className="text-[10px] text-slate-500">{subRender(r)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-400">{valueLabel}</div>
    </div>
  );
}
